import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './lib/auth';
import { LocationProvider } from './lib/location-context';
import { RequireAuth } from './components/RequireAuth';
import { RequirePermission } from './components/RequirePermission';
import { Layout } from './components/Layout';
import { LoginScreen } from './screens/LoginScreen';
import { SetPasswordScreen } from './screens/SetPasswordScreen';
import { OAuthConsentScreen } from './screens/OAuthConsentScreen';
import { CalendarScreen } from './screens/CalendarScreen';
import { TodayScreen } from './screens/TodayScreen';
import { ScanScreen } from './screens/ScanScreen';
import { SearchScreen } from './screens/SearchScreen';
import { BookingDetailScreen } from './screens/BookingDetailScreen';
import { VouchersScreen } from './screens/VouchersScreen';
import { VoucherDetailScreen } from './screens/VoucherDetailScreen';
import { GiftCardsScreen } from './screens/GiftCardsScreen';
import { GiftCardDetailScreen } from './screens/GiftCardDetailScreen';
import { MarketingScreen } from './screens/MarketingScreen';
import { PaymentsScreen } from './screens/PaymentsScreen';
import { SalesScreen } from './screens/SalesScreen';
import { RunsScreen } from './screens/RunsScreen';
import { SetupScreen } from './screens/SetupScreen';
import { ChatbotScreen } from './screens/ChatbotScreen';
import { RunReportScreen } from './screens/RunReportScreen';
import { TeamOutingsScreen } from './screens/TeamOutingsScreen';
import { TopPeriodsScreen } from './screens/TopPeriodsScreen';
import { NoAccessScreen } from './screens/NoAccessScreen';
import { useAuth } from './lib/auth';
import { homePath, NO_ACCESS_PATH } from './lib/home';

// Code-split: the Analyse page pulls in recharts, which we don't want in the
// main bundle that every other screen loads.
const AnalyticsScreen = lazy(() =>
  import('./screens/AnalyticsScreen').then((m) => ({ default: m.AnalyticsScreen })),
);

// '/' and anything unknown go to the account's own home: calendar for staff,
// Meldingen for an external partner login.
function HomeRedirect() {
  const { can } = useAuth();
  return <Navigate to={homePath(can)} replace />;
}

// The ordinary staff pages. One gate for all of them so a partner login
// cannot type its way onto the calendar.
function Staff({ children }: { children: ReactNode }) {
  return <RequirePermission permission="booking_staff_access">{children}</RequirePermission>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route
            path="/set-password"
            element={
              <RequireAuth>
                <SetPasswordScreen />
              </RequireAuth>
            }
          />
          {/* Supabase's OAuth 2.1 consent page (Authentication > OAuth Server > Authorization Path).
              Outside Layout: it is a one-question dialog another app sent the person to. */}
          <Route
            path="/oauth/consent"
            element={
              <RequireAuth>
                <OAuthConsentScreen />
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/calendar" element={<Staff><CalendarScreen /></Staff>} />
            <Route path="/today" element={<Staff><TodayScreen /></Staff>} />
            <Route path="/scan" element={<Staff><ScanScreen /></Staff>} />
            <Route path="/search" element={<Staff><SearchScreen /></Staff>} />
            <Route path="/vouchers" element={<Staff><VouchersScreen /></Staff>} />
            <Route path="/cadeaubonnen" element={<Staff><GiftCardsScreen /></Staff>} />
            <Route
              path="/marketing"
              element={
                <RequirePermission permission="booking_marketing">
                  <MarketingScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/payments"
              element={
                <RequirePermission permission="booking_view_transactions">
                  <PaymentsScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/sales"
              element={
                <RequirePermission permission="booking_view_sales">
                  <SalesScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/analytics"
              element={
                <RequirePermission permission="booking_view_analytics">
                  <Suspense fallback={<div className="screen muted">Laden…</div>}>
                    <AnalyticsScreen />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="/analytics/toppers"
              element={
                <RequirePermission permission="booking_view_analytics">
                  <TopPeriodsScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/runs"
              element={
                <RequirePermission permission="booking_manage_external_runs">
                  <RunsScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/meldingen"
              element={
                <RequirePermission permission="booking_report_external_runs">
                  <RunReportScreen />
                </RequirePermission>
              }
            />
            <Route
              path="/teamuitjes"
              element={
                <RequirePermission permission="booking_team_outings">
                  <TeamOutingsScreen />
                </RequirePermission>
              }
            />
            <Route path={NO_ACCESS_PATH} element={<NoAccessScreen />} />
            <Route
              path="/chatbot"
              element={
                <RequirePermission permission="support_manage_intents">
                  <ChatbotScreen />
                </RequirePermission>
              }
            />
            <Route path="/setup" element={<Staff><SetupScreen /></Staff>} />
            <Route path="/booking/:number" element={<Staff><BookingDetailScreen /></Staff>} />
            <Route path="/voucher/:code" element={<Staff><VoucherDetailScreen /></Staff>} />
            <Route path="/cadeaubon/:code" element={<Staff><GiftCardDetailScreen /></Staff>} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
          </Route>
        </Routes>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
